import { useState } from 'react';
import CustomModalOverlay from '@/components/common/Custom/CustomModalOverlay/CustomModalOverlay';
import SelectInput from '@/components/ui/SelectInput/SelectInput';
import TextArea from '@/components/ui/TextArea/TextArea';
import TextInput from '@/components/ui/TextInput/TextInput';
import { useModal } from '@/context/ModalContext';
import { TASK_TYPE_OPTIONS } from '../CreateNewTask/CreateNewTask';
import { priorityOptions } from '@/pages/projects/CreateProject/CreateProject';
import { useForm } from '@mantine/form';
import GlobalButton from '@/components/ui/GlobalButton';
// import IntakeLinks from "../IntakeModal/IntakeLinks/IntakeLinks";
// import IntakeComments from "../IntakeModal/IntakeComments/IntakeComments";
import IntakeService from '@/services/IntakeService';
// import useTeamsStore from "@/store/Teams";
import type { CreateIntakePayload } from '@/types';
import { useParams } from 'react-router-dom';
import { getErrorMessage } from '@/utils/helpers/helpers';

export const DEPARTMENT_OPTIONS = [
  { label: 'Frontend Engineering', value: 'Frontend Engineering' },
  { label: 'Backend Engineering', value: 'Backend Engineering' },
  { label: 'Data', value: 'Data' },
  { label: 'Product', value: 'Product' },
  { label: 'Design', value: 'Design' },
];

export default function CreateNewIntake() {
  const { isOpen, closeModal, modalData } = useModal();
  
  const { team_id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shaking, setShaking] = useState(false);
  const onSuccess = (modalData as any)?.onSuccess;

  const triggerShake = () => {
    setShaking(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShaking(true));
    });
  };

  const handleClose = () => {
    setErrorMessage('');
    closeModal('create-new-intake');
  };

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      acceptance_criteria: '',
      test_cases: '',
      intake_status: 'To-Do',
      priority: 'P0',
      intake_department: 'Product',
      intake_type: 'Bug',
    },
    validate: {
      title: (value: string) => (!value ? 'Title is required' : null),
    },
  });

  const handleCreateIntake = async () => {
    if (!team_id) {
      return;
    }

    if (!form.validate().hasErrors) {
      setIsLoading(true);
      setErrorMessage('');
      try {
        const payload: CreateIntakePayload = {
          title: form.values.title,
          description: form.values.description,
          acceptance_criteria: form.values.acceptance_criteria,
          test_cases: form.values.test_cases,
          intake_status: form.values.intake_status as any,
          priority: form.values.priority as any,
          intake_department: form.values.intake_department as any,
          intake_type: form.values.intake_type as any,
        };

        await IntakeService.createIntake(payload, team_id);
        form.reset();
        if (onSuccess) {
          await onSuccess();
        }
        handleClose();
      } catch (error) {
        console.error('Error creating intake:', error);
        setErrorMessage(getErrorMessage(error));
        triggerShake();
      } finally {
        setIsLoading(false);
      }
    } else {
    }
  };

  return (
    <CustomModalOverlay
      open={isOpen('create-new-intake')}
      width={'800px'}
      height="fit-content"
      onClose={handleClose}
      title={'Create New Intake'}
      subTitle={
        'Capture a request and route it to the right team for execution.'
      }
      withCloseButton
      footer={
        <div className="ml-auto flex items-end justify-end gap-3">
          <div className="w-fit">
            <GlobalButton
              variant="stroke"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </GlobalButton>
          </div>
          <div className="w-fit">
            <GlobalButton
              variant="purple"
              state={isLoading || !form.isValid() ? 'inactive' : 'default'}
              onClick={handleCreateIntake}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create New Intake'}
            </GlobalButton>
          </div>
        </div>
      }
    >
      <>
        <div className="mt-4">
          <TextInput
            label={'Title summary'}
            placeholder={'e.g. Fix login crash on iOS 17'}
            {...form.getInputProps('title')}
          />
          <div className="mt-4">
            <TextArea
              height="80px"
              label={'Task description'}
              placeholder={
                'Describe the issue or request in detail. Include expected outcome or current behavior'
              }
              {...form.getInputProps('description')}
              withMaxLength={false}
            />
          </div>
        </div>

        <div className="mt-4">
          <SelectInput
            label={'Priority level'}
            placeholder="Click to select priority level"
            options={priorityOptions}
            size="lg"
            value={form.values.priority}
            onChange={(val) => {
              form.setFieldValue('priority', val);
            }}
          />
        </div>

        <div className="mt-4">
          <SelectInput
            label={'Department'}
            placeholder="Select a department"
            options={DEPARTMENT_OPTIONS}
            size="lg"
            value={form.values.intake_department}
            onChange={(val) => {
              form.setFieldValue('intake_department', val);
            }}
            error={
              typeof form.errors.intake_department === 'string'
                ? form.errors.intake_department
                : undefined
            }
          />
        </div>

        <div className="mt-4">
          <SelectInput
            label={'What type of request is this?'}
            placeholder="Select a request type"
            options={TASK_TYPE_OPTIONS}
            size="lg"
            value={form.values.intake_type}
            onChange={(val) => {
              form.setFieldValue('intake_type', val);
            }}
            error={
              typeof form.errors.intake_type === 'string'
                ? form.errors.intake_type
                : undefined
            }
          />
        </div>

        {/* <div className="mt-4">
        <IntakeLinks intake={null} />
      </div> */}

        {/* ====== Acceptance criteria ======= */}

        <div className="mt-4">
          <TextArea
            label="Acceptance criteria"
            optionalLabel="Optional"
            height="100px"
            withMaxLength={false}
            placeholder={
              'Define measurable conditions to be met for this task to be considered complete.'
            }
            {...form.getInputProps('acceptance_criteria')}
          />
        </div>
        {/* ====== Test cases ======= */}
        <div className="mt-4">
          <TextArea
            label="Test Cases"
            optionalLabel="Optional"
            height="100px"
            withMaxLength={false}
            placeholder={
              'Document the steps and expected outcomes to verify that this task meets its acceptance criteria.'
            }
            {...form.getInputProps('test_cases')}
          />
        </div>

        {/* <div className="mt-4">
        <IntakeComments intake={null} />
      </div> */}

        {errorMessage && (
          <p
            className={`mt-2 text-sm text-error-2 ${shaking ? 'shake' : ''}`}
            onAnimationEnd={() => setShaking(false)}
          >
            {errorMessage}
          </p>
        )}
      </>
    </CustomModalOverlay>
  );
}
